// Kustomize-Components-format adapter API client (roadmap item 43).
//
// The Kustomize adapter mounts under `/kustomize/<project>/<repo>/v2/...`
// for the OCI-distribution endpoints that `kustomize build oci://…` talks
// to. The two browser-facing helpers below sit on the parallel
// `/kustomize/<project>/<repo>/components` surface — JSON-shaped data the
// UI renders for an operator inspecting which components a given repo
// holds — plus a YAML preview helper for the bundle layer's
// kustomization.yaml.
//
// All helpers key on slugs (project + repo) rather than UUIDs because the
// underlying registry surface is slug-keyed for protocol-compatibility
// with the kustomize CLI; the UI converts a `Repo` from `useRepoDetail`
// into the slug pair via `useProjects()`.

import useSWR from 'swr';

export interface KustomizeLayer {
  media_type: string;
  digest: string;
  size: number;
}

export interface KustomizeComponentEntry {
  component: string;
  tag: string;
  digest: string;
  total_size_bytes: number;
  layer_count: number;
  uploaded_at: string;
  media_type?: string;
}

export interface KustomizeComponentsResponse {
  project: string;
  repo: string;
  components: KustomizeComponentEntry[];
  pull_hint: string;
}

export interface KustomizeComponentDetailResponse {
  project: string;
  repo: string;
  component: string;
  tag: string;
  digest: string;
  size_bytes: number;
  uploaded_at: string;
  metadata: Record<string, unknown>;
  pull_hint: string;
}

const fetcher = (url: string) =>
  fetch(url, {
    credentials: 'include',
    headers: { Accept: 'application/json' },
  }).then((r) => {
    if (!r.ok) throw new Error(`${r.status} ${r.statusText}`);
    return r.json();
  });

// kustomization.yaml preview returns YAML text, not JSON. SWR still works
// fine with a non-JSON body — we just give it a text-aware fetcher.
const textFetcher = (url: string) =>
  fetch(url, {
    credentials: 'include',
    headers: { Accept: 'text/yaml, text/plain' },
  }).then(async (r) => {
    if (!r.ok) throw new Error(`${r.status} ${r.statusText}`);
    return r.text();
  });

export function useKustomizeComponents(projectSlug?: string, repoSlug?: string) {
  const url =
    projectSlug && repoSlug
      ? `/kustomize/${encodeURIComponent(projectSlug)}/${encodeURIComponent(repoSlug)}/components`
      : null;
  return useSWR<KustomizeComponentsResponse>(url, fetcher);
}

// encodeComponentPath joins component-name segments individually so
// component names containing slashes (`platform/redis`) round-trip safely
// through chi's wildcard matcher.
function encodeComponentPath(component: string): string {
  return component.split('/').map(encodeURIComponent).join('/');
}

export function useKustomizeComponentDetail(
  projectSlug: string | undefined,
  repoSlug: string | undefined,
  component: string | undefined,
  tag: string | undefined,
) {
  const url =
    projectSlug && repoSlug && component && tag
      ? `/kustomize/${encodeURIComponent(projectSlug)}/${encodeURIComponent(repoSlug)}/components/${encodeComponentPath(component)}/${encodeURIComponent(tag)}`
      : null;
  return useSWR<KustomizeComponentDetailResponse>(url, fetcher);
}

export function useKustomizationPreview(
  projectSlug: string | undefined,
  repoSlug: string | undefined,
  component: string | undefined,
  tag: string | undefined,
) {
  const url =
    projectSlug && repoSlug && component && tag
      ? `/kustomize/${encodeURIComponent(projectSlug)}/${encodeURIComponent(repoSlug)}/components/${encodeComponentPath(component)}/${encodeURIComponent(tag)}/kustomization`
      : null;
  return useSWR<string>(url, textFetcher);
}
